<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import CostTypeModel from '@models/CostType';
import debounce from 'lodash/debounce';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.cost_types'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'name',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', sortable: true },
        { key: 'name', label: this.$t('table_labels.name'), sortable: false },
        { key: 'client', label: this.$t('table_labels.client'), sortable: false },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      filters: {
        name: null,
      },
      isBusy: false,
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    updateFilter: debounce(function (value) {
      this.filters.name = value;
    }, 500),
    itemsProvider(ctx) {
      return CostTypeModel.orderBy(this.sortDir + ctx.sortBy)
        .where('name', this.filters.name)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.cost_type') }))) {
        this.$wait.start('cost type processing');
        new CostTypeModel({ id })
          .delete()
          .then(() => {
            this.$wait.end('cost type processing');
            this.$refs.asyncTable.refresh();
          })
          .catch(() => {
            this.$wait.end('cost type processing');
          });
      }
    },
    updateItem(item) {
      this.$nextTick(() => {
        this.$wait.start(`statusLoader${item.id}`);
        const costType = new CostTypeModel(Object.assign({}, item));
        costType.save().then(() => {
          this.$wait.end(`statusLoader${item.id}`);
        });
      });
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent
          :title="$t('titles.cost_types')"
          :button-text="$t('titles.create_cost_type')"
          @button-pressed="$router.push({ name: 'costTypes.create' })"
        ></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="12" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span
            >&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [$t('search.entities.name')].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.name" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter">&nbsp;</b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.name" @click="filters.name = ''">{{
              $t('actions.clear')
            }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <div id="cars-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('car type processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(client)="row">
          <div v-can:doAccountingOrSuperAdministrate :class="$style.statusContainer">
            <div v-show="!$wait.is(`statusLoader${row.item.id}`)">
              <b-form-checkbox v-model="row.item.client" :value="1" :unchecked-value="0" @change="updateItem(row.item)"> </b-form-checkbox>
            </div>

            <FontAwesomeIcon v-show="$wait.is(`statusLoader${row.item.id}`)" :class="$style.statuLoadingIcon" :icon="['fas', 'spinner']" pulse />
          </div>
          <div v-can:doAccountingOrSuperAdministrate.not>
            <FontAwesomeIcon v-if="parseInt(row.item.client, 10)" class="text-success" :icon="['fas', 'check-circle']" />
            <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
          </div>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'costTypes.edit', params: { id: row.item.id } }">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
<style lang="scss" module>
table {
  font-size: 0.7rem;
}

.statusContainer {
  padding-left: 1rem;
  margin: 0;
}

.statusLoadingIcon {
  margin: 0 auto;
}
</style>
